/**
 * RegisterForm
 *
 * Mirrors FOM doRegister() exactly:
 * - Fields: lname, fname, emp, email, phone, callSign, rank, inviteCode, password
 * - Name stored as "LastName, FirstName"
 * - Phone digits only (strip non-digits)
 * - POST { authAction:'register', employeeNo, email, phone, callSign, rank, name, passwordHash, inviteCode }
 * - res.pending → show pending message
 * - res.success && !pending → auto-approved, save session
 */
import { useState } from 'react'
import { sha256, workerPostV1 } from '../../api/worker'
import type { FomSession } from '../../stores/auth'

const RANKS = [
  'Director', 'Assistant Director', 'Lieutenant',
  'Sergeant', 'OIC', 'Officer', 'Dispatcher', 'Developer',
]

interface RegisterResponse {
  success: boolean
  pending?: boolean
  token?: string
  refreshToken?: string
  expiresIn?: number
  rank?: string
  employeeNo?: string
  name?: string
  message?: string
  error?: string
}

interface Props {
  onSuccess: (session: FomSession) => void  // auto-approved → parent shows disclaimer
  onPending: () => void
  onShowLogin: () => void
}

export default function RegisterForm({ onSuccess, onPending, onShowLogin }: Props) {
  const [form, setForm] = useState({
    lname: '', fname: '', emp: '', email: '',
    phone: '', cs: '', rank: '', invite: '',
    pw: '', pw2: '',
  })
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  function set(field: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  async function handleRegister() {
    const { lname, fname, emp, email, phone, cs, rank, invite, pw, pw2 } = form

    if (!lname || !fname || !emp || !email || !cs || !rank || !pw) {
      setError('All fields are required'); return
    }
    if (pw !== pw2) { setError('Passwords do not match'); return }
    if (pw.length < 4) { setError('Password must be at least 4 characters'); return }
    if (!invite) { setError('Invite code is required'); return }

    setLoading(true); setError('')
    try {
      const hash = await sha256(pw)
      const fullName = `${lname.trim()}, ${fname.trim()}`
      const res = await workerPostV1<RegisterResponse>('/api/v1/auth/register', {
        employeeNo: emp.trim(),
        email: email.trim(),
        phone: phone.replace(/\D/g, ''),
        callSign: cs.trim().toUpperCase(),
        rank,
        name: fullName,
        passwordHash: hash,
        inviteCode: invite.trim(),
      })

      if (res.success) {
        if (res.pending) {
          onPending()
        } else {
          // Auto-approved — pass session to parent, parent handles disclaimer
          const session: FomSession = {
            callSign: cs.trim().toUpperCase(),
            rank,
            employeeNo: emp.trim(),
            token: res.token ?? '',
            refreshToken: res.refreshToken ?? '',
            expiresAt: res.expiresIn ? Date.now() + res.expiresIn * 1000 : undefined,
            name: fullName,
            email: email.trim(),
            phone: phone.replace(/\D/g, ''),
          }
          onSuccess(session)
        }
      } else {
        const msg = res.message ?? res.error ?? 'Registration failed'
        if (msg.includes('Call sign already')) {
          setError('This call sign is already registered. Sign in instead.')
        } else if (msg.includes('Employee number already')) {
          setError('This employee number is already registered. Sign in instead.')
        } else if (msg.toLowerCase().includes('invite')) {
          setError('Invalid invite code. Ask your supervisor for the correct code.')
        } else {
          setError(msg)
        }
      }
    } catch {
      setError('Network error. Check your connection and try again.')
    }
    setLoading(false)
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box',
    background: 'var(--bg-deep)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '9px 12px',
    color: 'var(--text-primary)', fontSize: 13, outline: 'none',
  }
  const labelStyle: React.CSSProperties = {
    fontSize: 11, color: 'var(--text-muted)',
    fontWeight: 600, textTransform: 'uppercase',
    letterSpacing: 0.5, display: 'block', marginBottom: 4,
  }
  const fieldStyle: React.CSSProperties = { marginBottom: 10 }

  return (
    <div>
      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)',
          borderRadius: 8, padding: '10px 12px', fontSize: 13,
          color: '#ef4444', marginBottom: 12,
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={labelStyle}>Last Name</label>
          <input value={form.lname} onChange={set('lname')} placeholder="Last name" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>First Name</label>
          <input value={form.fname} onChange={set('fname')} placeholder="First name" style={inputStyle} />
        </div>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Employee Number</label>
        <input value={form.emp} onChange={set('emp')} placeholder="Employee #" style={inputStyle} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={labelStyle}>Email</label>
          <input type="email" value={form.email} onChange={set('email')} placeholder="Email" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Phone</label>
          <input type="tel" value={form.phone} onChange={set('phone')} placeholder="Phone" style={inputStyle} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={labelStyle}>Call Sign</label>
          <input value={form.cs} onChange={set('cs')} placeholder="e.g. S-01" style={inputStyle} autoCapitalize="characters" />
        </div>
        <div>
          <label style={labelStyle}>Rank</label>
          <select value={form.rank} onChange={set('rank')} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select rank</option>
            {RANKS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <div style={fieldStyle}>
        <label style={labelStyle}>Invite Code</label>
        <input value={form.invite} onChange={set('invite')} placeholder="Ask your supervisor for the code" style={inputStyle} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
        <div>
          <label style={labelStyle}>Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPw ? 'text' : 'password'}
              value={form.pw} onChange={set('pw')}
              placeholder="Min 4 characters"
              style={{ ...inputStyle, paddingRight: 36 }}
            />
            <button type="button" onClick={() => setShowPw(v => !v)}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14 }}>
              {showPw ? '🙈' : '👁️'}
            </button>
          </div>
        </div>
        <div>
          <label style={labelStyle}>Confirm Password</label>
          <input
            type={showPw ? 'text' : 'password'}
            value={form.pw2} onChange={set('pw2')}
            placeholder="Re-enter password"
            style={inputStyle}
            onKeyDown={e => e.key === 'Enter' && handleRegister()}
          />
        </div>
      </div>

      <button onClick={handleRegister} disabled={loading} style={{
        width: '100%', padding: 12,
        background: 'linear-gradient(135deg,#c9a84c,#dbb960)',
        border: 'none', borderRadius: 8,
        color: '#1a2744', fontWeight: 700, fontSize: 14,
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.8 : 1,
      }}>
        {loading ? '⏳ Creating Account…' : 'Create Account'}
      </button>

      <p style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: 'var(--text-muted)' }}>
        Already have an account?{' '}
        <span style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }} onClick={onShowLogin}>
          Sign in
        </span>
      </p>
    </div>
  )
}
